import { Input } from "@/components/ui/input";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";
import { DocumentTypeSelectorProps } from "../types";
import { useTranslation } from "react-i18next";
import { useDocumentTypes } from "@/hooks/useDocumentTypes";
import { Loader2 } from "lucide-react";

export function DocumentTypeSelector({
  selectedTypeId,
  searchValue,
  showDropdown,
  className = "",
  placeholder,
  onChange,
  onSelect,
  onDropdownToggle,
  onClickInput
}: Omit<DocumentTypeSelectorProps, "documentTypes">) {
  const { t } = useTranslation();
  const defaultPlaceholder = t("common:documents.search_placeholder");
  const { data: documentTypes = [], isLoading } = useDocumentTypes();
  // Find the selected document type name
  const selectedTypeName = selectedTypeId
    ? documentTypes.find((type) => type.id === selectedTypeId)?.name
    : "";

  return (
    <div className={`relative ${className}`}>
      <Input
        placeholder={placeholder || defaultPlaceholder}
        className="w-full"
        disabled={isLoading}
        // Show the selected type name if there's a selection and no search in progress
        value={selectedTypeId ? selectedTypeName : searchValue}
        onChange={(e) => {
          if (!isLoading) {
            onChange(e.target.value);
          }
        }}
        onClick={() => {
          if (!isLoading) {
            onClickInput();
          }
        }}
      />
      {showDropdown && (
        <div className="absolute z-10 w-full mt-1 bg-popover rounded-md shadow-md border">
          <Command className="rounded-lg border shadow-md">
            <CommandInput
              placeholder={placeholder || defaultPlaceholder}
              value={searchValue}
              onValueChange={onChange}
              className="border-none focus:ring-0"
            />
            <CommandList className="max-h-[200px] overflow-auto">
              {isLoading ? (
                <div className="flex items-center justify-center p-4">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <>
                  <CommandEmpty>
                    {t("common:documents.no_results")}
                  </CommandEmpty>
                  <CommandGroup>
                    {documentTypes
                      .filter(
                        (type) =>
                          !searchValue ||
                          (type.name &&
                            type.name
                              .toLowerCase()
                              .includes(searchValue.toLowerCase()))
                      )
                      .map((type) => (
                        <CommandItem
                          key={type.id}
                          onSelect={() => {
                            if (type.name) {
                              onSelect(type.id, type.name);
                            }
                            onDropdownToggle(false);
                          }}
                          className="cursor-pointer"
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              selectedTypeId === type.id
                                ? "opacity-100"
                                : "opacity-0"
                            )}
                          />
                          {type.name}
                        </CommandItem>
                      ))}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </div>
      )}
    </div>
  );
}
